/* eslint-disable prettier/prettier */
/* eslint-disable no-shadow */
import React from 'react';

import { InterestPointType, InterestPointTypeValues } from 'models/repossession/enums/repossession.enums';
import { Coordinate } from 'models/shared/types/basic';
import { CircleLayer, HeatmapLayer } from 'mapbox-gl';
import { Marker } from 'react-map-gl';
import Supercluster from 'supercluster';
import { Typography } from 'design_system/src';
import { momentLocal } from 'models/shared/helpers/dates';

const HEATMAP_MAX_ZOOM_LEVEL = 15;

export const hotspotHeatLayerDefinition: HeatmapLayer = {
	id: 'tile_interest_points_heat',
	type: 'heatmap',
	maxzoom: HEATMAP_MAX_ZOOM_LEVEL,
	paint: {
		'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0.01, 1, 1],
		'heatmap-intensity': [
			'interpolate',
			['linear'],
			['zoom'],
			0,
			1,
			HEATMAP_MAX_ZOOM_LEVEL,
			3,
		],
		'heatmap-color': [
			'interpolate',
			['exponential', 30],
			['heatmap-density'],
			0,
			'rgba(111, 157, 236,0)',
			0.5,
			'rgba(111, 157, 236, 0.9)',
			0.8,
			'rgba(236, 237, 239, 0.9)',
			0.85,
			'rgba(255, 216, 171, 0.9)',
			0.9,
			'rgba(223, 32, 64, 0.7)',
			1,
			'rgba(254, 138, 2, 0.9)',
		],
		'heatmap-radius': [
			'interpolate',
			['linear'],
			['zoom'],
			0,
			20,
			HEATMAP_MAX_ZOOM_LEVEL,
			40,
		],
		'heatmap-opacity': [
			'interpolate',
			['linear'],
			['zoom'],
			HEATMAP_MAX_ZOOM_LEVEL - 2,
			1,
			HEATMAP_MAX_ZOOM_LEVEL,
			0,
		],
	},
};

export const hotspotTileLayerDefinition: CircleLayer = {
	id: 'tile_interest_points_circles',
	type: 'circle',
	minzoom: HEATMAP_MAX_ZOOM_LEVEL - 2,
	paint: {
		'circle-radius': 5,
		'circle-color': 'rgb(0,0,255)',
		'circle-stroke-color': 'white',
		'circle-stroke-width': 1,
		'circle-opacity': [
			'interpolate',
			['linear'],
			['zoom'],
			HEATMAP_MAX_ZOOM_LEVEL - 2,
			0,
			HEATMAP_MAX_ZOOM_LEVEL,
			1,
		],
	},
};

export interface PlainLocation {
	vehicleId: string;
	loanId: string;
	repossessionId: string;
	interestPointId?: string;
	externalId?: string;
	locationType: InterestPointType;
	coordinates: Coordinate;
	createdAt?: string;
	id: string;
}

export interface TableInfo {
	_id: string;
	loanId: string;
	internalId: string;
	lastRecovery?: string;
	recoveriesAmount: number;
	lastLocation?: string;
	lastTileLocation?: string;
	nextAction: string;
}

export enum RepossessionTabs {
	repossession = 'repossession',
	routes = 'routes',
}

export const RepossessionTabsValues = {
	[RepossessionTabs.repossession]: 'Por recuperar',
	[RepossessionTabs.routes]: 'Rutas',
};

export const activeInterestPointTypes = [
	InterestPointType.address,
	InterestPointType.geolocation,
	InterestPointType.signalTileVehicle,
	InterestPointType.noSignalTileVehicle,
];

export enum CreateRouteSteps {
	allRepossessions = 1,
	filterRepossessions = 2,
	selectInterestPoints = 3,
	createRoute = 4,
}

export const CreateRouteStepsValues = new Map([
	[CreateRouteSteps.allRepossessions, 'Seleccionar motos'],
	[CreateRouteSteps.filterRepossessions, 'Peluquear motos'],
	[
		CreateRouteSteps.selectInterestPoints,
		'Escoger puntos de interés por cada moto',
	],
	[CreateRouteSteps.createRoute, 'Crear ruta'],
]);

export const hoursOptions = [
	{ label: '0:00a.m. - 1:00a.m.', value: 0 },
	{ label: '1:00a.m. - 2:00a.m.', value: 1 },
	{ label: '2:00a.m. - 3:00a.m.', value: 2 },
	{ label: '3:00a.m. - 4:00a.m.', value: 3 },
	{ label: '4:00a.m. - 5:00a.m.', value: 4 },
	{ label: '5:00a.m. - 6:00a.m.', value: 5 },
	{ label: '6:00a.m. - 7:00a.m.', value: 6 },
	{ label: '7:00a.m. - 8:00a.m.', value: 7 },
	{ label: '8:00a.m. - 9:00a.m.', value: 8 },
	{ label: '9:00a.m. - 10:00a.m.', value: 9 },
	{ label: '10:00a.m. - 11:00a.m.', value: 10 },
	{ label: '11:00a.m. - 12:00p.m.', value: 11 },
	{ label: '12:00p.m. - 1:00p.m.', value: 12 },
	{ label: '1:00p.m. - 2:00p.m.', value: 13 },
	{ label: '2:00p.m. - 3:00p.m.', value: 14 },
	{ label: '3:00p.m. - 4:00p.m.', value: 15 },
	{ label: '4:00p.m. - 5:00p.m.', value: 16 },
	{ label: '5:00p.m. - 6:00p.m.', value: 17 },
	{ label: '6:00p.m. - 7:00p.m.', value: 18 },
	{ label: '7:00p.m. - 8:00p.m.', value: 19 },
	{ label: '8:00p.m. - 9:00p.m.', value: 20 },
	{ label: '9:00p.m. - 10:00p.m.', value: 21 },
	{ label: '10:00p.m. - 11:00p.m.', value: 22 },
	{ label: '11:00p.m. - 12:00a.m.', value: 23 },
];

export const createClusterMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	callback: Function,
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;

	return (
		<Marker
			key={`cluster-${cluster.id}`}
			longitude={longitude}
			latitude={latitude}
			onClick={() => callback(cluster)}
		>
			<div className="cluster_1">
				<div className="cluster_2">
					<div className="cluster_3 dim_xxxl">
						<Typography weight="600" scale="small" className="cluster_count">
							{cluster.properties.point_count}
						</Typography>
					</div>
				</div>
			</div>
		</Marker>
	);
};

export const createPointMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	point: PlainLocation,
	selected:boolean,
	open:boolean,
	visitCount:number,
	callback: Function,
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;
	const Icon = InterestPointTypeValues[point.locationType].icon;

	return (
		<Marker
			key={point.id}
			longitude={longitude}
			latitude={latitude}
			onClick={() => {
				callback(point);
			}}
		>
			<div
				className={`br_xxl main_marker cursor_pointer ${
					InterestPointTypeValues[point.locationType].color
				} ${selected ? 'selected_marker' : ''} ${
					open ? 'open_marker' : ''
				}`}
			>
				<div className="p_y_xxs p_x_md flex_center z_index_1 pos_relative">
					<Icon className="dim_lg icon" />
					<div className="p_x_md flex_center_col">
						<Typography weight="600" scale="xsmall">
							{momentLocal(point.createdAt).format('DD/MM/YYYY')}
						</Typography>
						<Typography weight="400" scale="xsmall">
							{momentLocal(point.createdAt).format('HH:mm')}
						</Typography>
					</div>
				</div>
				{visitCount ? (
					<div className="pos_absolute br_circle dim_md marker_counter bg_red_300">
						<Typography
							weight="400"
							scale="xxsmall"
							className="text_neutral_0 pos_absolute center"
						>
							{visitCount}
						</Typography>
					</div>
				) : null}
				<span className="arrow" />
			</div>
		</Marker>
	);
};

export const createClusterHotspotMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	callback: Function,
	point: PlainLocation
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;
	const size = Math.min(cluster.properties.point_count*4/100,4);

	return (
		<Marker
			key={`cluster-${cluster.id}`}
			longitude={longitude}
			latitude={latitude}
			onClick={() => callback(point, true)}
		>
					<div className="bg_neutral_900 br_circle cursor_pointer" style= {{height:`${size}rem`, width:`${size}rem`}}>
						<Typography weight="600" scale="xsmall" textColor="neutral_0" className="center pos_absolute">
							{size<1?'':cluster.properties.point_count}
						</Typography>
					</div>
		</Marker>
	);
};
